import type { Region } from '~~/core/entities/Region';

import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { slugify } from '~~/utils';
import { REGION_SERVICE } from '~~/utils/symbols';

export function useRegionInfos(filters: Ref<{ regions?: string[] }>) {
	const route = useRoute();
	const regionService = inject(REGION_SERVICE)!;

	const key = computed(() => `region:${filters.value.regions?.[0]}`);

	const regionName = computed<string>(() => {
		return regionInfo.value?.hero?.title || '';
	});

	const hasInvalidFiltersToShowRegionInfo = () => {
		const filtersFromPath = PropertyFiltersPresenter.getFiltersFromPath(
			route.fullPath
		);

		const allowedFilterKeys = ['locationSlug', 'transactionType'];

		const hasInvalidFilter = Object.entries(filtersFromPath).some(
			([key, value]) =>
				!allowedFilterKeys.includes(key) &&
				(Array.isArray(value) ? value.length > 0 : Boolean(value))
		);

		return hasInvalidFilter;
	};

	const { data: regionInfo, refresh: refreshRegionInfo } =
		useAsyncData<Region | null>(
			key.value,
			async () => {
				const slug = slugify(filters.value.regions?.[0] || '');
				if (
					!slug ||
					Object.keys(route.query).length > 0 ||
					hasInvalidFiltersToShowRegionInfo()
				) {
					return null;
				}

				const [region] = await regionService.getOne({ slug });
				return region;
			},
			{
				default: () => null,
				...getCacheAsyncDataConfig<Region | null>(30 * 60 * 1000),
				lazy: false,
				server: true,
				immediate: true,
				watch: [() => filters.value]
			}
		);

	return { regionInfo, refreshRegionInfo, regionName };
}
